import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface LoginPageProps {
  onLogin: (did: string) => void;
  onRegister: (username: string, displayName: string) => Promise<unknown>;
  onDemoLogin: () => void;
}

export function LoginPage({ onLogin, onRegister, onDemoLogin }: LoginPageProps) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [did, setDid] = useState('');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!did.trim()) return;
    onLogin(did.trim());
    navigate('/');
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !displayName.trim()) return;

    setLoading(true);
    setError('');
    try {
      await onRegister(username.trim().toLowerCase(), displayName.trim());
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDemo = () => {
    onDemoLogin();
    navigate('/');
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{
        zIndex: 100,
        background: '#f3f4f6',
        backgroundImage: "url('https://www.transparenttextures.com/patterns/graphy.png')",
      }}
    >
      <div
        className="p-8 border max-w-md w-full text-center"
        style={{
          background: 'white',
          borderColor: '#d1d5db',
          boxShadow: '3px 3px 0 rgba(0,0,0,0.15)',
        }}
      >
        {/* Logo */}
        <div className="text-4xl font-mono font-bold mb-2" style={{ color: '#3b5998', letterSpacing: '-0.05em' }}>
          [cb]
        </div>
        <h1 className="text-2xl font-bold mb-1" style={{ color: '#1f2937' }}>SYMSVOX</h1>
        <p className="text-sm italic mb-6" style={{ color: '#6b7280' }}>
          "Social in the streets, BSL-4 in the sheets"
        </p>

        {/* Tabs */}
        <div className="flex gap-2 mb-4" style={{ borderBottom: '1px solid #d1d5db' }}>
          <button
            onClick={() => setMode('login')}
            className="flex-1 py-2 font-bold"
            style={{
              color: mode === 'login' ? '#3b5998' : '#9ca3af',
              borderBottom: mode === 'login' ? '2px solid #3b5998' : '2px solid transparent',
            }}
          >
            Sign In
          </button>
          <button
            onClick={() => setMode('register')}
            className="flex-1 py-2 font-bold"
            style={{
              color: mode === 'register' ? '#3b5998' : '#9ca3af',
              borderBottom: mode === 'register' ? '2px solid #3b5998' : '2px solid transparent',
            }}
          >
            Register Agent
          </button>
        </div>

        {/* Form */}
        {mode === 'login' ? (
          <form onSubmit={handleLogin}>
            <input
              type="text"
              value={did}
              onChange={(e) => setDid(e.target.value)}
              placeholder="Enter DID (e.g. did:sym:123...)"
              className="w-full border p-2 mb-4 text-sm font-mono"
              style={{ borderColor: '#d1d5db', background: '#f9fafb' }}
            />
          </form>
        ) : (
          <form onSubmit={handleRegister} className="space-y-3 mb-4">
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              className="w-full border p-2 text-sm"
              style={{ borderColor: '#d1d5db', background: '#f9fafb' }}
            />
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Display Name"
              className="w-full border p-2 text-sm"
              style={{ borderColor: '#d1d5db', background: '#f9fafb' }}
            />
            {error && <p className="text-xs" style={{ color: '#ef4444' }}>{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full font-bold py-2 text-sm disabled:opacity-40"
              style={{ background: '#3b5998', color: 'white' }}
            >
              {loading ? 'Creating...' : 'Create Account'}
            </button>
          </form>
        )}

        {/* Demo Button */}
        <button
          onClick={handleDemo}
          className="w-full font-bold py-3 text-sm flex items-center justify-center gap-2 shadow-sm transition-all hover:shadow-md"
          style={{ background: '#3b5998', color: 'white' }}
          onMouseEnter={e => { e.currentTarget.style.background = '#29487d'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#3b5998'; }}
        >
          <span>⚡</span> Try the Sandbox (Demo)
        </button>

        {/* Footer */}
        <div className="mt-4 text-xs" style={{ color: '#9ca3af' }}>
          Running Symbiosis Kernel v9.2 • Security Level: Unbonded
        </div>
      </div>
    </div>
  );
}
